import React, { useState,useEffect, useContext } from 'react';
import { Grid, Box, Typography,Card,Container,CircularProgress } from '@mui/material';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { collection, getDocs,Timestamp,query,where } from 'firebase/firestore';
import { db } from '../firebase';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AppContext from '../AppContext';
import { startOfDay,endOfDay } from "date-fns";
import { DataGrid,GridToolbar } from '@mui/x-data-grid';
import { format, isValid } from "date-fns";

const FinancialReport = () => {
  const [financialData,setFinancialData]=useState([]);
  const [orderData, setOrderData] = useState([]);
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const {shopId, language, t = (key) => key}=useContext(AppContext);
  const [loading,setLoading]=useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const cashBookRef = collection(db, 'cashbook');
        const orderRef = collection(db, 'orders');
        
        const qCashbook = query(
          cashBookRef,
          where('timestamp', '>=', Timestamp.fromDate(startOfDay(fromDate))),
          where('timestamp', '<=', Timestamp.fromDate(endOfDay(toDate || fromDate))),
          where('shopId', '==', shopId)
        );

        const qOrders = query(
          orderRef,
          where('orderDate', '>=', Timestamp.fromDate(startOfDay(fromDate))),
          where('orderDate', '<=', Timestamp.fromDate(endOfDay(toDate || fromDate))), 
          where('shopId', '==', shopId)
        );

        const [cashbookSnapshot, ordersSnapshot] = await Promise.all([
          getDocs(qCashbook),
          getDocs(qOrders)
        ]);

        const financeData = cashbookSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const ordersData = ordersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setFinancialData(financeData);
        setOrderData(ordersData);
      } catch (error) {
        console.error('Error fetching data: ', error);
      } finally {
        setLoading(false);
      }

      
    };

    fetchData();
  }, [fromDate, toDate,shopId]);

    // Formatter for currency
  const currencyFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'MMK',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
 
   // Prepare data for Bar Chart (Total Amount by Category)
   const categoryData = orderData.reduce((acc, curr) => {
     curr.cartItems.forEach(item => {
       acc[item.category] = (acc[item.category] || 0) + item.totalPrice;
     });
     return acc;
   }, {});
 
   const barChartCategoryData = Object.entries(categoryData).map(([category, total]) => ({
     category,
     total,
   }));

  // Prepare data for Pie Chart (Income by Payment Method)
  const paymentMethodData = financialData.reduce((acc, curr) => {
    const method = curr.paymentMethod;
    const amount = curr.transactionType === 'income' ? curr.amount : 0;

    acc[method] = (acc[method] || 0) + amount;
    return acc;
  }, {});

  const pieChartData = Object.entries(paymentMethodData).map(([method, amount]) => ({
    name: method,
    value: amount,
  }));

  // Prepare data for Bar Graph (Total Income/Outcome by Date)
  const dateData = financialData.reduce((acc, curr) => {
    const date = new Date(curr.timestamp.seconds * 1000).toLocaleDateString();
    const amount = curr.amount;
   // console.log(date);
    if (!acc[date]) {
      acc[date] = { income: 0, outcome: 0,refund:0,intermediary:0 };
    }

    if (curr.transactionType === 'income') {
      acc[date].income += amount;
    } 
    if (curr.transactionType === 'outcome') {
      acc[date].outcome += amount;
    }
    if(curr.transactionType==='refund'){
      acc[date].refund += amount;
    }
    

    return acc;
  }, {});

  const barChartData = Object.entries(dateData).map(([date, { income, outcome, refund }]) => ({
    date: new Date(date).toLocaleDateString('en-GB'),
    income,
    outcome,
    refund,
  }));

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const formatDate = (timestamp) => {
    const date = new Date(timestamp.toDate());
    if (isValid(date)) {
      return format(date, "dd-MM-yy h:mm a");
    }
    return "Invalid Date";
  };

  // Calculate the total amount based on transaction type
const totalAmount = financialData.reduce((sum, order) => {
  
  if (order.transactionType === 'income' || order.transactionType === 'intermediary') {
    
    return sum + order.amount;
  } else{
    
    return sum - order.amount;
  }
  
}, 0);

  const columns = [
    { field: 'orderId', headerName: t('Order ID'), width: 150 },
    { field: 'timestamp', headerName: t('Date'), width: 150 },
    { field: 'description', headerName: t('Description'), width: 300 },
    { field: 'amount', headerName: t('Amount'), width: 130},
    { field: 'transactionType', headerName: t('In/Out'), width: 130},
    { field: 'paymentMethod', headerName: t('Payment Method'), width: 180 },
  ];

  const rows = financialData.map((order) => ({
    id: order.id, // Ensure 'id' is unique for each row
    orderId: order.orderId,
   // timestamp: new Date(order.timestamp.toDate()).toLocaleDateString('en-GB'),
    timestamp: formatDate(order.timestamp),
    description: order.description,
    amount: formatter.format(order.amount),
    transactionType: order.transactionType === 'income' 
        ? t('In') 
        : order.transactionType === 'intermediary' 
            ? t('Int') 
            : t('Out'),
    paymentMethod: order.paymentMethod
   
  }));

  // Append the total row at the end of rows
rows.push({
  id: 'total', // Ensure 'id' is unique
  orderId: t('Total'), // Label for total row
  timestamp: '',
  description: '',
  amount: formatter.format(totalAmount), // Display the total amount
  transactionType: '',
  paymentMethod: ''
});


  const balanceChartData = Object.entries(dateData).map(([date, { income, outcome, refund }]) => ({
    date: new Date(date).toLocaleDateString('en-GB'),
    balance: income - (outcome + refund),
  }));

  const pieColors = ['#0B84A5', '#F6C85F', '#6F4E7C', '#9DD866', '#CA472F', '#FFA056'];



  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
    <Container>
    <Box>
      <Typography variant="h4" gutterBottom>{t('Financial Report')}</Typography>
      <Box mb={2} mt={3}>
          <Grid container spacing={2}>
            <Grid item>
              <DatePicker
                label={t('From Date')}
                value={fromDate}
                onChange={(newValue) => setFromDate(newValue)}
                format="dd/MM/yyyy"
              />
            </Grid>
            <Grid item>
              <DatePicker
                label={t('To Date')}
                value={toDate}
                onChange={(newValue) => setToDate(newValue)}
                format="dd/MM/yyyy"
              />
            </Grid>
          </Grid>
        </Box>
        {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <CircularProgress />
        </Box>
        ) : (
        <>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Card variant='outlined' sx={{ padding: 2 }}>
            <Typography variant="h6">{t('Income by Payment Method')}</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieChartData}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={100}
                  label
                >
                  {pieChartData.map((entry, index) => (
                    <Cell key={`cell-${entry.name}`} fill={pieColors[index % pieColors.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [currencyFormatter.format(value), name]} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant='outlined' sx={{ padding: 2 }}>
            <Typography variant="h6">{t('Total Transaction by Date')}</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={barChartData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis tickFormatter={(value) => currencyFormatter.format(value)} />
                <Tooltip formatter={(value, name) => [currencyFormatter.format(value), name]} />
                <Legend />
                <Bar dataKey="income" name={t('Income')} fill="#2E7D32" />
                <Bar dataKey="outcome" name={t('Outcome')} fill="#C62828" />
                <Bar dataKey="refund" name={t('Refund')} fill="#F9A825" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card variant='outlined' sx={{ padding: 2 }}>
            <Typography variant="h6">{t('Total Balance by Date')}</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={balanceChartData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis tickFormatter={(value) => currencyFormatter.format(value)} />
                <Tooltip formatter={(value, name) => [currencyFormatter.format(value), name]} />
                <Legend />
                <Bar dataKey="balance" name={t('Balance')} fill="#1565C0" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Grid>

          <Grid item xs={12} md={6}>
            <Card variant='outlined' sx={{ padding: 2 }}>
              <Typography variant="h6">{t('Total Amount by Category')}</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={barChartCategoryData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="category" />
                  <YAxis tickFormatter={(value) => currencyFormatter.format(value)} />
                  <Tooltip formatter={(value, name) => [currencyFormatter.format(value), name]} />
                  <Legend />
                  <Bar dataKey="total" name={t('Total Amount')} fill="#6A1B9A" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Grid>
        <Grid item xs={12}>
              <Box sx={{ height: 400, width: '100%' }}>
                <DataGrid
                  rows={rows}
                  columns={columns}
                  pageSize={5}
                  rowsPerPageOptions={[5, 10, 25]}
                  loading={loading}
                  slots={{ toolbar: GridToolbar }}
                  disableSelectionOnClick
                />
              </Box>
            </Grid>
      </Grid>
      </>
      )}
    </Box>
   
    </Container>
    </LocalizationProvider>
  );
};

export default FinancialReport;
